// "Create invoice" window for the ADMIN panel (Orders tab button and Customers account panel button).
// Self-contained palette (same approach as remittanceUi.js) so App.js does not have to be imported:
// everything App.js owns (ALL_TERMS, the catalog, the rep list, the company info, the invoice viewer) comes
// in as a prop. The window never writes anything itself: every write goes through adminCreateInvoice.js,
// which saves to Supabase FIRST (awaited) and mirrors locally only afterwards.
import React, { useState, useEffect, useRef, useMemo } from "react";
import { loadAdminInvoiceAccount, performCreateAdminInvoice, resendAdminInvoiceEmail, checkSavedAdminInvoice } from "./adminCreateInvoice";
import {
  eligibleDealerAccounts, resolveCreditedRep, buildCatalogLookup, validatePercentInput, computeAdminPricing, pricingProblem,
  makeAdminInvoiceId, sourceText, validEmail, HOUSE_REP_LABEL, similarNamedReps, MAX_UNITS_PER_LINE,
} from "./adminInvoicePure";
import { formatPct, round2, samePct } from "./invoiceDiscount";

const K = {
  ink: "#0F172A", inkMid: "#334155", inkLight: "#64748B", border: "#E2E8F0", surface: "#F8FAFC", white: "#FFFFFF",
  red: "#B91C1C", redLight: "#FEE2E2", redBorder: "#FCA5A5", amber: "#92400E", amberBg: "#FEF3C7", amberBorder: "#FCD34D",
  green: "#166534", greenBg: "#DCFCE7", greenBorder: "#86EFAC", blue: "#1560BD", blueLight: "#E8F0FE",
};
const box = (bg, border, fg) => ({ background: bg, border: "1px solid " + border, borderRadius: 8, padding: "10px 12px", marginBottom: 10, fontSize: 12, color: fg, lineHeight: 1.5 });
const label = { display: "block", fontSize: 11, fontWeight: 600, color: K.inkMid, marginBottom: 4 };
const input = { width: "100%", padding: "8px 10px", border: "1px solid " + K.border, borderRadius: 8, fontSize: 13, color: K.ink, background: K.white, outline: "none", boxSizing: "border-box", fontFamily: "inherit" };
const btnPrimary = { background: K.blue, color: K.white, border: "none", padding: "9px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" };
const btnSecondary = { background: "transparent", color: K.ink, border: "1px solid " + K.border, padding: "8px 14px", borderRadius: 8, fontSize: 13, cursor: "pointer" };
const linkBtn = { background: "none", border: "none", padding: 0, color: K.blue, fontSize: 12, cursor: "pointer", textDecoration: "underline" };
const money = (n) => "$" + round2(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const defaultApi = { loadAccount: loadAdminInvoiceAccount, create: performCreateAdminInvoice, resendEmail: resendAdminInvoiceEmail, checkSaved: checkSavedAdminInvoice };
const HOUSE_VALUE = "__house__";
// "A", "A and B", "A, B and C" (the invoice numbers a start-over left unconfirmed).
const listNumbers = (arr) => (arr.length <= 1 ? (arr[0] || "") : arr.slice(0, -1).join(", ") + " and " + arr[arr.length - 1]);

// A small searchable dropdown (typing filters the list). Private copy: the rep portal's is not exported.
function SearchSelect({ value, onChange, options, placeholder, disabled, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const sel = options.find((o) => o.value === value);
  const needle = q.trim().toLowerCase();
  const filtered = needle ? options.filter((o) => (o.label + " " + (o.sub || "")).toLowerCase().includes(needle)) : options;
  return (
    <div style={{ position: "relative" }}>
      <input
        aria-label={ariaLabel} disabled={disabled} style={input} autoComplete="off"
        value={open ? q : (sel ? sel.label : "")} placeholder={placeholder}
        onFocus={() => { setOpen(true); setQ(""); }}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && !disabled && (
        <div role="listbox" style={{ position: "absolute", top: "100%", left: 0, right: 0, maxHeight: 220, overflowY: "auto", background: K.white, border: "1px solid " + K.border, borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 20, marginTop: 2 }}>
          {filtered.length === 0 && <div style={{ padding: "10px 12px", fontSize: 12, color: K.inkLight }}>No matches</div>}
          {filtered.slice(0, 100).map((o) => (
            <div key={o.value} role="option" aria-selected={o.value === value}
              onMouseDown={(e) => { e.preventDefault(); onChange(o.value); setOpen(false); setQ(""); }}
              style={{ padding: "8px 12px", fontSize: 12, cursor: "pointer", display: "flex", justifyContent: "space-between", gap: 8, background: o.value === value ? K.blueLight : K.white, borderBottom: "1px solid " + K.border }}>
              <span>{o.label}</span>
              {o.right ? <span style={{ color: K.inkLight, whiteSpace: "nowrap" }}>{o.right}</span> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

let lineKey = 0;
const newLine = () => ({ key: ++lineKey, sku: "", qty: "1" });
const qtyOk = (q) => { const s = String(q == null ? "" : q).trim(); return /^\d+$/.test(s) && Number(s) >= 1 && Number(s) <= MAX_UNITS_PER_LINE; };
const QTY_MESSAGE = "Enter a whole number of units from 1 to " + MAX_UNITS_PER_LINE.toLocaleString("en-US") + ".";
/**
 * @param {object} props
 *  accounts          the admin accounts list (eligible dealers are filtered here)
 *  repOptions        [{id, name, email}] active, approved reps (profiles.full_name as name)
 *  termsList         ALL_TERMS ([{id,label}])
 *  catalogReady      true only when the live catalog loaded without error
 *  getCatalog        () => ({ products, kits }) the LIVE catalog (never seed data)
 *  initialAccountId  preselected dealer (profiles.id) or null
 *  getMpc            () => company info object for the emailed PDF
 *  getOutstanding    (dealerId, company) => number, the dealer's unpaid invoices total (credit note)
 *  onClose / onViewInvoice(inv) / onOpenInOrders(id) / onCreated(inv)
 *  nudge             { n, accountId }: bumped by the parent when a Create invoice button is clicked while this window is
 *                    already open or folded; it unfolds the window (draft kept) and, for another dealer, says so
 *  api               injectable { loadAccount, create, resendEmail, checkSaved } (tests)
 */
export function AdminCreateInvoiceModal({ accounts, repOptions, termsList, catalogReady, getCatalog, initialAccountId, getMpc, getOutstanding, onClose, onViewInvoice, onOpenInOrders, onCreated, nudge, api }) {
  const apiRef = useRef(api || defaultApi);
  apiRef.current = api || defaultApi;
  const repOptionsRef = useRef(repOptions || []);
  repOptionsRef.current = repOptions || [];
  const termsRef = useRef(termsList || []);
  termsRef.current = termsList || [];

  const dealers = useMemo(() => eligibleDealerAccounts(accounts), [accounts]);
  const [dealerId, setDealerId] = useState(() => {
    const id = initialAccountId ? String(initialAccountId) : "";
    return id && eligibleDealerAccounts(accounts).some((a) => a.supabaseId === id) ? id : "";
  });
  const [acct, setAcct] = useState({ status: "idle" });
  const [reloadTick, setReloadTick] = useState(0);
  const [pctText, setPctText] = useState("0");
  const [shownPct, setShownPct] = useState(0);
  const [termsId, setTermsId] = useState("");
  const [repSel, setRepSel] = useState("");
  const [repInfo, setRepInfo] = useState(null);
  const [lines, setLines] = useState(() => [newLine()]);
  const [catalogSnap, setCatalogSnap] = useState(() => {
    if (!catalogReady || typeof getCatalog !== "function") return null;
    const c = getCatalog() || {};
    return buildCatalogLookup(c.products, c.kits);
  });
  useEffect(() => {
    if (!catalogReady || catalogSnap || typeof getCatalog !== "function") return;
    const c = getCatalog() || {};
    setCatalogSnap(buildCatalogLookup(c.products, c.kits));
  }, [catalogReady, catalogSnap, getCatalog]);

  const invoiceIdRef = useRef(null);   // ONE id per open, kept across retries, rotated only after a verified success
  const getInvoiceId = () => { if (!invoiceIdRef.current) invoiceIdRef.current = makeAdminInvoiceId(); return invoiceIdRef.current; };
  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);       // null | { ok:false, ... } | { ok:true, ... }
  const [notice, setNotice] = useState("");         // amber "the figures were refreshed" note
  const [emailState, setEmailState] = useState(null);
  const [emailBusy, setEmailBusy] = useState(false);
  // The rate needs an explicit choice when the profile and the customer settings disagree or the settings row
  // is missing: null = not chosen yet, "ack" = the admin ticked "I checked: use X%", "typed" = the admin typed a rate.
  const [rateChoice, setRateChoice] = useState(null);
  // An attempt whose outcome was NOT confirmed (timeout / dropped connection / gateway error): the id of that
  // invoice, and whether the admin has confirmed that they looked in Orders and it is not there. While
  // pendingId is set and not confirmed BOTH Create buttons stay disabled.
  const [pendingId, setPendingId] = useState(null);
  const [checkedOrders, setCheckedOrders] = useState(false);
  const pendingRef = useRef(null);
  const [peek, setPeek] = useState(false);   // the window is folded into a bar so the Orders list behind it can be read
  // "Check the database now": a strict lookup of the pending invoice number (null | { state, ... }).
  const [dbCheck, setDbCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const checkingRef = useRef(false);
  // "Start over with a new invoice number": asks for confirmation first; oldNumber remembers the abandoned number.
  const [confirmRestart, setConfirmRestart] = useState(false);
  // Every number abandoned by a start-over (an ARRAY: starting over twice must not forget the first number).
  const [oldNumbers, setOldNumbers] = useState([]);
  // Closing the window while a number is unconfirmed asks first (the number is forgotten when the window closes).
  const [confirmClose, setConfirmClose] = useState(false);
  const confirmCloseRef = useRef(null);
  // The insert layer is waiting before it looks the pending number up again: { until } drives the visible countdown.
  const [waitInfo, setWaitInfo] = useState(null);
  const [, setTick] = useState(0);
  // A click on a Create invoice button while this window is open or folded (see the nudge prop).
  const [openNote, setOpenNote] = useState("");
  const dealerIdRef = useRef(dealerId);
  dealerIdRef.current = dealerId;
  const nudgeSeen = useRef(nudge && nudge.n ? nudge.n : 0);
  useEffect(() => {
    if (!nudge || !nudge.n || nudge.n === nudgeSeen.current) return undefined;
    nudgeSeen.current = nudge.n;
    setPeek(false);   // unfold: the draft is exactly as it was
    const wanted = nudge.accountId ? String(nudge.accountId) : "";
    if (wanted && wanted !== dealerIdRef.current) {
      setOpenNote("Finish or close the open invoice first.");
      const t = setTimeout(() => setOpenNote(""), 8000);
      return () => clearTimeout(t);
    }
    setOpenNote("");
    return undefined;
  }, [nudge]);
  useEffect(() => {
    if (confirmClose && confirmCloseRef.current && typeof confirmCloseRef.current.scrollIntoView === "function") confirmCloseRef.current.scrollIntoView({ block: "nearest" });
  }, [confirmClose]);
  useEffect(() => {
    if (!waitInfo) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 250);
    return () => clearInterval(t);
  }, [waitInfo]);

  // Fresh strict read of the dealer when one is picked (and on Retry). Depends ONLY on the dealer and the
  // retry counter: the props it uses are read through refs so a parent re-render never restarts the read.
  useEffect(() => {
    if (!dealerId) { setAcct({ status: "idle" }); return undefined; }
    let live = true;
    setAcct({ status: "loading", id: dealerId });
    Promise.resolve(apiRef.current.loadAccount(dealerId, termsRef.current)).then((res) => {
      if (!live) return;
      if (!res || !res.ok) { setAcct({ status: "error", id: dealerId, error: (res && res.error) || "The dealer account could not be read." }); return; }
      setAcct({ status: "ready", id: dealerId, data: res });
      setPctText(res.rate.pct > 0 ? formatPct(res.rate.pct) : "0");
      setShownPct(res.rate.pct);
      setRateChoice(null);   // a new dealer (or a re-read) always needs its own rate check
      setTermsId(res.terms.defaultId || "");
      const r = resolveCreditedRep(res.assignedRep, repOptionsRef.current);
      setRepInfo({ ...r, assigned: res.assignedRep || "" });
      setRepSel(r.ok ? r.rep.id : "");
      setResult(null);
      setNotice("");
    }, (e) => {
      console.warn("[AdminCreateInvoiceModal] loading the dealer threw:", e?.message || e);
      if (live) setAcct({ status: "error", id: dealerId, error: e?.message || "The dealer account could not be read." });
    });
    return () => { live = false; };
  }, [dealerId, reloadTick]);

  const ready = acct.status === "ready" && acct.id === dealerId;
  const data = ready ? acct.data : null;
  const bySku = catalogSnap ? catalogSnap.bySku : {};

  // ── live figures (the same functions that will persist them) ──────────────────
  const pctRes = validatePercentInput(pctText);
  const lineProblems = lines.map((l) => {
    if (!l.sku) return "";
    if (!bySku[l.sku]) return "This product is no longer in the catalog. Remove it.";
    if (!qtyOk(l.qty)) return QTY_MESSAGE;
    return "";
  });
  const submitLines = lines.filter((l) => l.sku).map((l) => ({ sku: l.sku, qty: Number(String(l.qty).trim()) }));
  const previewItems = lines.filter((l) => l.sku && bySku[l.sku] && qtyOk(l.qty)).map((l) => ({ price: bySku[l.sku].price, qty: Number(String(l.qty).trim()) }));
  const pricing = computeAdminPricing(previewItems, pctRes.ok ? pctRes.pct : 0);
  const overridden = ready && pctRes.ok && !samePct(pctRes.pct, shownPct);

  let problem = "";
  if (!dealerId) problem = "Choose the dealer to invoice.";
  else if (!ready) problem = "Wait for the dealer's account to load.";
  else if (!catalogReady || !catalogSnap) problem = "The product catalog has not loaded.";
  else if (submitLines.length === 0) problem = "Add at least one item.";
  else if (lineProblems.some(Boolean)) problem = "Fix the highlighted item lines.";
  else if (!pctRes.ok) problem = pctRes.error;
  else if (data && data.rate && data.rate.needsAck && rateChoice === null) problem = "Confirm the discount rate: tick \"I checked\" under the Discount box, or type the rate yourself.";
  else if (!termsId) problem = "Choose the payment terms.";
  else if (!repSel) problem = "Choose who is credited with this invoice (a sales rep, or No rep).";
  else problem = pricingProblem(pricing) || "";
  // An earlier attempt for THIS invoice number was not confirmed: nothing may be sent again until the admin has looked in Orders.
  const awaitingCheck = !!pendingId && !checkedOrders;

  const selectedRep = repSel && repSel !== HOUSE_VALUE ? (repOptionsRef.current || []).find((r) => r.id === repSel) : null;
  const similarReps = selectedRep ? similarNamedReps(selectedRep, repOptionsRef.current) : [];
  const dealerEmail = data && data.profile && data.profile.email ? String(data.profile.email) : "";
  const canEmail = validEmail(dealerEmail);
  const outstanding = ready && typeof getOutstanding === "function" ? Number(getOutstanding(dealerId, data.profile.company)) || 0 : 0;
  const creditLimit = ready ? data.creditLimit : 0;

  const oldList = listNumbers(oldNumbers);
  // Seconds left of the pre-retry wait; once the wait is over (<= 0) the lookup and the save follow, and the box says so.
  const waitLeft = waitInfo ? Math.ceil((waitInfo.until - Date.now()) / 1000) : 0;
  // Cancel and the x both come here. While a number is unconfirmed (pendingRef) closing would forget it although the
  // invoice may have been saved, so the first press only asks; "Close anyway" is the only way out of that state.
  const close = () => {
    if (busyRef.current || checkingRef.current) return;
    if (pendingRef.current) { setConfirmClose(true); return; }
    if (onClose) onClose();
  };
  const closeAnyway = () => { if (busyRef.current || checkingRef.current) return; setConfirmClose(false); if (onClose) onClose(); };

  // meta.ownAbort: this window's own timeout aborted the request (short retry wait); otherwise a gateway / 5xx / dropped
  // connection (long retry wait). meta.emailDealer: the attempt asked for the email, so a found invoice is emailed.
  const markUncertain = (id, meta) => { pendingRef.current = { id, checked: false, ownAbort: !!(meta && meta.ownAbort), emailDealer: !!(meta && meta.emailDealer) }; setPendingId(id); setCheckedOrders(false); setDbCheck(null); setConfirmRestart(false); setConfirmClose(false); };
  const clearUncertain = () => { pendingRef.current = null; setPendingId(null); setCheckedOrders(false); setPeek(false); setDbCheck(null); setConfirmRestart(false); setConfirmClose(false); };
  const confirmChecked = (v) => { if (pendingRef.current) pendingRef.current.checked = !!v; setCheckedOrders(!!v); };

  // A save the database has confirmed (a fresh insert, a deduped retry, or a row found by "Check the database now").
  const acceptSaved = (res) => {
    clearUncertain();
    invoiceIdRef.current = null;   // verified success: the next invoice gets a new number
    setEmailState(res.email || { status: "not_requested" });
    setResult(res);
    if (onCreated) { try { onCreated(res.cacheInvoice || res.invoice); } catch (e) { console.warn("[AdminCreateInvoiceModal] onCreated threw:", e?.message || e); } }
  };

  const submit = async (emailDealer) => {
    if (busyRef.current || checkingRef.current || problem) return;
    if (pendingRef.current && !pendingRef.current.checked) return;   // an unconfirmed earlier attempt: look in Orders first
    busyRef.current = true;
    setBusy(true);
    setResult(null);
    setNotice("");
    setConfirmClose(false);   // an unanswered "close without confirming" question must not outlive the attempt it was about
    const prev = pendingRef.current;
    const wasUncertain = !!prev;
    // After a gateway / 5xx / dropped connection the original request may still be running on the server, so the
    // retry waits much longer (and shows a countdown); after this window's own timeout-and-abort the short wait is enough.
    const afterGateway = wasUncertain && !prev.ownAbort;
    try {
      const res = await apiRef.current.create({
        invoiceId: getInvoiceId(), dealerId, lines: submitLines, pctInput: pctText, shownAccountPct: shownPct, shownBySku: bySku,
        repChoice: repSel === HOUSE_VALUE ? { mode: "house" } : { mode: "rep", id: repSel },
        paymentTermsId: termsId, emailDealer: !!emailDealer, mpc: typeof getMpc === "function" ? getMpc() : {},
        afterUncertain: wasUncertain, afterGateway,
        onWait: (ms) => { setWaitInfo({ until: Date.now() + (Number(ms) || 0) }); },
      });
      if (res && res.ok) {
        acceptSaved(res);
      } else {
        const r = res || { ok: false, message: "The invoice could not be saved. Nothing was saved.", uncertain: false };
        if (r.uncertain) markUncertain(invoiceIdRef.current || "", { ownAbort: !!r.ownAbort, emailDealer: !!emailDealer });
        if (r.code === "rate_changed" && r.freshRate && r.freshRate.ok) {
          setAcct((a) => (a.status === "ready" ? { ...a, data: { ...a.data, rate: r.freshRate } } : a));
          setRateChoice(null);
          setShownPct(r.freshRate.pct);
          setPctText(r.freshRate.pct > 0 ? formatPct(r.freshRate.pct) : "0");
          setNotice(r.message);
        } else if (r.code === "catalog_changed" && Array.isArray(r.freshCatalog)) {
          const bySkuNew = {};
          r.freshCatalog.forEach((row) => { bySkuNew[row.sku] = row; });
          setCatalogSnap({ list: r.freshCatalog, bySku: bySkuNew });
          setNotice(r.message);
        } else {
          setResult(r);
        }
      }
    } catch (e) {
      console.warn("[AdminCreateInvoiceModal] create threw:", e?.message || e);
      markUncertain(invoiceIdRef.current || "", { ownAbort: false, emailDealer: !!emailDealer });
      setResult({ ok: false, uncertain: true, message: "Something unexpected went wrong while saving (" + (e?.message || "unknown error") + "). Invoice " + (invoiceIdRef.current || "") + " MAY have been created: open Orders and look for it before trying again." });
    } finally {
      busyRef.current = false;
      setBusy(false);
      setWaitInfo(null);
    }
  };

  // "Check the database now": ask portal_orders itself whether the pending invoice number exists. Found = the invoice
  // is saved (success path, built from the STORED row); not found / could not check never creates anything.
  const checkDb = async () => {
    if (checkingRef.current || busyRef.current || !pendingRef.current) return;
    const pend = pendingRef.current;
    checkingRef.current = true;
    setChecking(true);
    setDbCheck(null);
    try {
      const fn = apiRef.current.checkSaved;
      if (typeof fn !== "function") { setDbCheck({ state: "could_not_check", message: "The database check is not available in this window. Reload the page and look in Orders." }); return; }
      const res = await fn({ invoiceId: pend.id, emailDealer: !!pend.emailDealer, mpc: typeof getMpc === "function" ? getMpc() : {} });
      if (res && res.ok && res.state === "found" && res.result && res.result.ok) { acceptSaved(res.result); return; }
      if (res && res.ok && res.state === "found_unreadable") { setDbCheck({ state: "found_unreadable", total: res.total, customer: res.customer }); return; }
      if (res && res.ok && (res.state === "not_found" || res.state === "in_flight")) { setDbCheck({ state: res.state }); return; }
      setDbCheck({ state: (res && res.state) || "could_not_check", message: (res && res.message) || "The database could not be checked. That does NOT mean the invoice is missing." });
    } catch (e) {
      console.warn("[AdminCreateInvoiceModal] the database check threw:", e?.message || e);
      setDbCheck({ state: "could_not_check", message: "The database could not be checked (" + (e?.message || "unknown error") + "). That does NOT mean the invoice is missing." });
    } finally {
      checkingRef.current = false;
      setChecking(false);
    }
  };

  // Explicit "Start over with a new invoice number": the unconfirmed number is abandoned (never deleted), a fresh number is
  // made on the next Create, and the window keeps telling the admin which old number still has to be looked at in Orders.
  const startOver = () => {
    if (busyRef.current || checkingRef.current || !pendingRef.current) return;
    const old = pendingRef.current.id || pendingId || "";
    invoiceIdRef.current = null;
    clearUncertain();
    setResult(null);
    if (old) setOldNumbers((list) => (list.includes(old) ? list : [...list, old]));
  };

  const resend = async () => {
    if (!result || !result.ok || emailBusy) return;
    setEmailBusy(true);
    try {
      const r = await apiRef.current.resendEmail({ invoice: result.invoice, mpc: typeof getMpc === "function" ? getMpc() : {} });
      setEmailState(r || { status: "failed", error: "No answer from the email service." });
    } catch (e) {
      console.warn("[AdminCreateInvoiceModal] resend threw:", e?.message || e);
      setEmailState({ status: "failed", error: e?.message || "The email could not be sent." });
    } finally { setEmailBusy(false); }
  };

  const createAnother = () => {
    setResult(null); setEmailState(null); setNotice("");
    setLines([newLine()]); setDealerId(""); setAcct({ status: "idle" }); setRepSel(""); setRepInfo(null);
    setRateChoice(null); clearUncertain(); setOldNumbers([]);
    invoiceIdRef.current = null;
  };

  const setLine = (key, patch) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const removeLine = (key) => setLines((ls) => (ls.length <= 1 ? [newLine()] : ls.filter((l) => l.key !== key)));

  const overlay = { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: 12 };
  const card = { background: K.white, borderRadius: 14, width: "min(780px, 100%)", maxHeight: "92vh", overflowY: "auto", padding: "22px 24px", boxSizing: "border-box" };

  // ── success screen ───────────────────────────────────────────────────────────
  if (result && result.ok) {
    const inv = result.invoice;
    const es = emailState || { status: "not_requested" };
    return (
      <div style={overlay} role="dialog" aria-label="Invoice created">
        <div style={card}>
          <div style={{ fontSize: 18, fontWeight: 700, color: K.green, marginBottom: 4 }}>Invoice created</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: K.ink, fontFamily: "monospace", marginBottom: 12 }}>{inv.id}</div>
          {result.fromCheck && <div style={box(K.greenBg, K.greenBorder, K.green)} role="status">The database confirms that this invoice was saved by your earlier attempt. Nothing was duplicated.</div>}
          {result.deduped && !result.fromCheck && <div style={box(K.amberBg, K.amberBorder, K.amber)}>This invoice had already been saved by an earlier attempt, so nothing was duplicated.</div>}
          {oldNumbers.length === 1 && <div style={box(K.amberBg, K.amberBorder, K.amber)} role="status" aria-label="Old invoice numbers">Earlier you started over and left invoice {oldNumbers[0]} unconfirmed. Look for {oldNumbers[0]} in Orders to be sure it was not saved too.</div>}
          {oldNumbers.length > 1 && <div style={box(K.amberBg, K.amberBorder, K.amber)} role="status" aria-label="Old invoice numbers">Earlier you started over {oldNumbers.length} times and left invoices {oldList} unconfirmed. Look for each of them ({oldList}) in Orders to be sure none of them was saved too.</div>}
          <div style={{ fontSize: 13, color: K.inkMid, lineHeight: 1.8, marginBottom: 12 }}>
            <div><strong>Dealer:</strong> {inv.customer}</div>
            <div><strong>Terms:</strong> {inv.paymentTerms}</div>
            <div><strong>Credited to:</strong> {inv.rep === HOUSE_REP_LABEL ? "No rep (house account): nobody earns commission" : inv.rep}</div>
            <div><strong>Subtotal:</strong> {money(inv.discount ? inv.subtotal : inv.total)}</div>
            {inv.discount && <div><strong>{inv.discount.label} ({formatPct(inv.discount.pct)}%):</strong> -{money(inv.discount.amount)}</div>}
            <div style={{ fontSize: 15, color: K.ink }}><strong>Total:</strong> {money(inv.total)}</div>
          </div>
          {es.status === "sent" && <div style={box(K.greenBg, K.greenBorder, K.green)}>Emailed to {es.to}.</div>}
          {es.status === "sent_no_pdf" && (
            <div style={box(K.amberBg, K.amberBorder, K.amber)} role="alert">
              The email to {es.to} was sent WITHOUT the invoice PDF attached (the PDF could not be made), so the dealer only got the invoice details in the message. Use Resend, or View / Print and send it yourself.
              <div style={{ marginTop: 6 }}><button style={btnSecondary} disabled={emailBusy} onClick={resend}>{emailBusy ? "Sending..." : "Resend email"}</button></div>
            </div>
          )}
          {es.status === "failed" && (
            <div style={box(K.redLight, K.redBorder, K.red)} role="alert">
              The invoice is saved, but the email to {es.to || "the dealer"} was NOT sent: {es.error}
              <div style={{ marginTop: 6 }}><button style={btnSecondary} disabled={emailBusy} onClick={resend}>{emailBusy ? "Sending..." : "Resend email"}</button></div>
            </div>
          )}
          {es.status === "not_requested" && (
            <div style={box(K.surface, K.border, K.inkMid)}>
              No email was sent (you chose Create invoice). {inv.customerEmail ? <button style={linkBtn} disabled={emailBusy} onClick={resend}>{emailBusy ? "Sending..." : "Email it to " + inv.customerEmail + " now"}</button> : null}
            </div>
          )}
          {result.mirrored === "failed" && <div style={box(K.amberBg, K.amberBorder, K.amber)}>The invoice is saved in the database, but this device's list could not be updated. Reload the page to see it in Orders.</div>}
          <div style={{ fontSize: 11, color: K.inkLight, marginBottom: 14 }}>Whether the dealer has paid is recorded per device, like every other invoice. The invoice has no shipping line and cannot be adjusted yet: void it and create a new one if it needs to change.</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button style={btnPrimary} onClick={() => { if (onViewInvoice) onViewInvoice(result.cacheInvoice || inv); if (onClose) onClose(); }}>View / Print</button>
            <button style={btnSecondary} onClick={() => { if (onOpenInOrders) onOpenInOrders(inv.id); if (onClose) onClose(); }}>Open in Orders</button>
            <button style={btnSecondary} onClick={createAnother}>Create another</button>
            <button style={btnSecondary} onClick={close}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  // ── the form ──────────────────────────────────────────────────────────────────
  const dealerOptions = dealers.map((a) => ({ value: a.supabaseId, label: a.company, right: a.contact && a.contact !== "—" ? a.contact : "" }));
  // The chosen dealer may stop being eligible while the window is open (paused, deleted...): keep its name in the
  // Dealer box (from the loaded profile, else from the accounts list) instead of going blank, and say so.
  const dealerGone = !!dealerId && !dealers.some((a) => a.supabaseId === dealerId);
  if (dealerGone) {
    const known = (accounts || []).find((a) => a && a.supabaseId === dealerId);
    const fallbackLabel = (data && data.profile && data.profile.company) || (known && known.company) || "This dealer";
    dealerOptions.unshift({ value: dealerId, label: fallbackLabel, right: "" });
  }
  const productOptions = (catalogSnap ? catalogSnap.list : []).map((p) => ({ value: p.sku, label: "[" + p.sku + "] " + p.name + (p.isKit ? " (KIT)" : ""), right: money(p.price) }));
  const rateInfo = data ? data.rate : null;
  const accountPct = rateInfo ? rateInfo.pct : 0;
  const termsOptions = data ? data.terms.options : [];
  const creditOver = ready && creditLimit > 0 && outstanding + (pricing.total || 0) > creditLimit;
  const pctWords = (p) => (Number(p) > 0 ? formatPct(p) + "%" : "none");

  // Folded: the Orders list behind the window can be read; the window (and its invoice number) is still here.
  if (peek && pendingId) {
    return (
      <div style={{ position: "fixed", left: 12, right: 12, bottom: 12, zIndex: 1100, background: K.white, border: "2px solid " + K.amberBorder, borderRadius: 12, padding: "12px 14px", boxShadow: "0 8px 24px rgba(0,0,0,0.25)", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }} role="dialog" aria-label="Create invoice (folded)">
        <div style={{ flex: 1, minWidth: 220, fontSize: 13, color: K.amber }}>
          <strong>Invoice {pendingId} is not confirmed.</strong> Look for it in the Orders list.
        </div>
        <button style={btnPrimary} onClick={() => setPeek(false)}>Back to the invoice window</button>
      </div>
    );
  }

  return (
    <div style={overlay} role="dialog" aria-label="Create invoice">
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: K.ink }}>Create invoice</div>
            <div style={{ fontSize: 12, color: K.inkLight }}>For a dealer account. The dealer's discount rate is applied automatically.</div>
          </div>
          <button aria-label="Close" style={{ background: "none", border: "none", fontSize: 22, color: K.inkLight, cursor: busy || checking ? "not-allowed" : "pointer", lineHeight: 1 }} disabled={busy || checking} onClick={close}>×</button>
        </div>

        {openNote && <div style={box(K.amberBg, K.amberBorder, K.amber)} role="status" aria-label="An invoice is already open">{openNote}</div>}
        {oldNumbers.length === 1 && <div style={box(K.amberBg, K.amberBorder, K.amber)} role="status" aria-label="Old invoice number">You started over with a new invoice number. The old number {oldNumbers[0]} was never confirmed: look for {oldNumbers[0]} in the Orders list. If it is there, that invoice exists and is not changed by this window.</div>}
        {oldNumbers.length > 1 && <div style={box(K.amberBg, K.amberBorder, K.amber)} role="status" aria-label="Old invoice number">You started over with a new invoice number {oldNumbers.length} times. The old numbers {oldList} were never confirmed: look for each of them ({oldList}) in the Orders list. If any of them is there, that invoice exists and is not changed by this window.</div>}
        {!catalogReady && (
          <div style={box(K.amberBg, K.amberBorder, K.amber)} role="alert">The product catalog has not finished loading (or failed to load), so invoices cannot be created yet. Close this window and try again in a moment. Prices are never taken from stored defaults.</div>
        )}
        {catalogReady && catalogSnap && catalogSnap.list.length === 0 && (
          <div style={box(K.amberBg, K.amberBorder, K.amber)}>The product catalog has no active products.</div>
        )}

        <div style={{ marginBottom: 12 }}>
          <label style={label}>Dealer</label>
          <SearchSelect ariaLabel="Dealer" value={dealerId} onChange={(v) => { if (!busyRef.current && !pendingRef.current) { setDealerId(v); setResult(null); setNotice(""); } }} options={dealerOptions} placeholder="Search for a dealer account..." disabled={busy || !!pendingId} />
          {dealers.length === 0 && <div style={{ fontSize: 11, color: K.inkLight, marginTop: 4 }}>No approved dealer accounts are available.</div>}
          {pendingId && <div style={{ fontSize: 11, color: K.amber, marginTop: 4 }} data-testid="dealer-locked">The dealer cannot be changed while invoice {pendingId} is not confirmed: that number may already exist for this dealer. Confirm it below, or use "Start over with a new invoice number".</div>}
          {dealerGone && <div style={{ ...box(K.amberBg, K.amberBorder, K.amber), marginTop: 6, marginBottom: 0 }} role="status">This dealer account is no longer an approved, active dealer (it may have been paused or changed since the window opened). The account is checked again when you press Create, and the invoice will not be saved if it is not eligible.</div>}
        </div>

        {dealerId && acct.status === "loading" && <div style={box(K.surface, K.border, K.inkMid)}>Checking the dealer's account (discount, terms, assigned rep)...</div>}
        {dealerId && acct.status === "error" && (
          <div style={box(K.redLight, K.redBorder, K.red)} role="alert">
            {acct.error}
            <div style={{ marginTop: 6 }}><button style={btnSecondary} onClick={() => setReloadTick((n) => n + 1)}>Retry</button></div>
          </div>
        )}

        {ready && (
          <>
            <div style={{ fontSize: 12, color: K.inkMid, marginBottom: 12 }}>
              Bill to <strong>{data.profile.company}</strong>{dealerEmail ? " · " + dealerEmail : " · no email on file"}
            </div>

            {/* Discount */}
            {/* alignItems flex-end: when the Account rate text sits beside the Discount box it lines up with the input, and when it wraps below it needs nothing: no width-dependent switch */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 6 }}>
              <div style={{ width: 150 }}>
                <label style={label} htmlFor="aci-pct">Discount %</label>
                <input id="aci-pct" aria-label="Discount percent" type="number" step="0.01" min="0" max="99.99" style={{ ...input, ...(overridden ? { border: "1px solid " + K.amberBorder } : {}) }} value={pctText} onChange={(e) => { setPctText(e.target.value); setRateChoice("typed"); }} disabled={busy} />
              </div>
              <div style={{ flex: 1, minWidth: 220, fontSize: 12, color: K.inkMid, paddingBottom: 9 }} data-testid="account-rate-block">
                {accountPct > 0
                  ? <>Account rate: <button style={linkBtn} disabled={busy} onClick={() => { setPctText(formatPct(accountPct)); setRateChoice("ack"); }} title="Use the account's own rate">{formatPct(accountPct)}%</button> (from {sourceText(rateInfo.source)})</>
                  : <>Account rate: <button style={linkBtn} disabled={busy} onClick={() => { setPctText("0"); setRateChoice("ack"); }}>none</button> (no discount set on the account)</>}
              </div>
            </div>
            {overridden && <div style={box(K.amberBg, K.amberBorder, K.amber)}>This invoice's discount differs from the account rate ({accountPct > 0 ? formatPct(accountPct) + "%" : "none"}). It applies to this invoice only; the account is not changed.</div>}
            {rateInfo && rateInfo.needsAck && (
              <div style={box(K.amberBg, K.amberBorder, K.amber)} role="alert" aria-label="Check the discount rate">
                {rateInfo.settingsFound === false
                  ? <>No customer-settings row was found for this dealer, so the rate below comes from the account profile: the profile says {pctWords(rateInfo.profilePct)}; the customer settings say nothing. Rep invoices and the dealer's own checkout read the customer settings.</>
                  : <>The account profile says {pctWords(rateInfo.profilePct)} but the customer settings say {pctWords(rateInfo.settingsPct)}. Rep invoices and the dealer's own checkout use the customer settings, so {pctWords(rateInfo.pct)} is filled in. Check Edit account if that is wrong.</>}
                <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, cursor: busy ? "not-allowed" : "pointer", fontWeight: 600 }}>
                  <input type="checkbox" aria-label={"I checked: use " + formatPct(accountPct) + "%"} checked={rateChoice === "ack"} disabled={busy} onChange={(e) => { if (e.target.checked) { setPctText(accountPct > 0 ? formatPct(accountPct) : "0"); setRateChoice("ack"); } else setRateChoice(null); }} />
                  I checked: use {formatPct(accountPct)}%
                </label>
                <div style={{ marginTop: 4 }}>Or type the rate you want in the Discount box.</div>
              </div>
            )}

            {/* Credited rep + terms */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <label style={label} htmlFor="aci-rep">Credited rep (earns the commission)</label>
                <select id="aci-rep" aria-label="Credited rep" style={input} value={repSel} onChange={(e) => setRepSel(e.target.value)} disabled={busy}>
                  <option value="">Choose...</option>
                  {(repOptions || []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  <option value={HOUSE_VALUE}>No rep (house account): nobody earns commission</option>
                </select>
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <label style={label} htmlFor="aci-terms">Payment terms</label>
                <select id="aci-terms" aria-label="Payment terms" style={input} value={termsId} onChange={(e) => setTermsId(e.target.value)} disabled={busy}>
                  {termsOptions.map((t) => <option key={t.id} value={t.id}>{t.display}</option>)}
                </select>
              </div>
            </div>
            {repInfo && repInfo.ok && repInfo.how === "fuzzy" && <div style={{ fontSize: 11, color: K.inkLight, marginBottom: 6 }}>The account's assigned rep "{repInfo.assigned}" was matched to {repInfo.rep.name}. Change it if that is wrong.</div>}
            {repInfo && repInfo.ok && repInfo.how === "exact" && <div style={{ fontSize: 11, color: K.inkLight, marginBottom: 6 }}>Pre-filled with the account's assigned rep. Clear it by choosing No rep.</div>}
            {repInfo && !repInfo.ok && (
              <div style={box(K.amberBg, K.amberBorder, K.amber)}>
                {repInfo.reason === "unassigned" ? "This account has no assigned rep." : repInfo.reason === "ambiguous" ? 'The account\'s assigned rep "' + repInfo.assigned + '" matches more than one active rep.' : 'The account\'s assigned rep "' + repInfo.assigned + '" is not one of the active reps.'} Choose who is credited, or No rep.
              </div>
            )}
            {selectedRep && <div style={{ fontSize: 11, color: K.inkLight, marginBottom: 6 }}>Commission is calculated on the net total after discount and goes only to {selectedRep.name}. The dealer's assigned rep can still see the invoice.</div>}
            {similarReps.length > 0 && (
              <div style={box(K.amberBg, K.amberBorder, K.amber)} role="alert" aria-label="Similar rep name">
                Another sales rep has a similar name ({similarReps.map((r) => r.name).join(", ")}). Commission and invoice lists match reps by first name and last initial, so this invoice may also show up for {similarReps.length === 1 ? "that rep" : "those reps"} (and commission may show for both) until the names differ. Check the Commission tab before paying.
              </div>
            )}
            {repSel === HOUSE_VALUE && <div style={{ fontSize: 11, color: K.inkLight, marginBottom: 6 }}>House account: this invoice counts toward no rep's sales or commission.</div>}
            {data.terms.fallback && <div style={{ fontSize: 11, color: K.amber, marginBottom: 6 }}>This account has no approved payment terms on file, so every term is offered.</div>}

            {/* Items */}
            <div style={{ margin: "14px 0 6px", fontSize: 12, fontWeight: 700, color: K.ink }}>Items (quantities in units, price per unit)</div>
            {lines.map((l, i) => {
              const row = l.sku ? bySku[l.sku] : null;
              const lineTotal = row && qtyOk(l.qty) ? row.price * Number(String(l.qty).trim()) : 0;
              return (
                <div key={l.key} style={{ marginBottom: 8 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 260px", minWidth: 200 }}>
                      <SearchSelect ariaLabel={"Product " + (i + 1)} value={l.sku} onChange={(v) => setLine(l.key, { sku: v })} options={productOptions} placeholder="Search by SKU or name..." disabled={busy || !catalogReady} />
                    </div>
                    <input aria-label={"Units " + (i + 1)} type="number" min="1" step="1" style={{ ...input, width: 80 }} value={l.qty} onChange={(e) => setLine(l.key, { qty: e.target.value })} disabled={busy} />
                    <div style={{ width: 130, fontSize: 12, color: K.inkMid, textAlign: "right" }}>{row ? money(row.price) + " each · " + money(lineTotal) : ""}</div>
                    <button aria-label={"Remove item " + (i + 1)} style={{ ...btnSecondary, padding: "6px 10px" }} disabled={busy} onClick={() => removeLine(l.key)}>×</button>
                  </div>
                  {lineProblems[i] && <div style={{ fontSize: 11, color: K.red, marginTop: 2 }}>{lineProblems[i]}</div>}
                </div>
              );
            })}
            <button style={{ ...btnSecondary, marginBottom: 12 }} disabled={busy || !catalogReady} onClick={() => setLines((ls) => [...ls, newLine()])}>+ Add item</button>

            {/* Totals */}
            <div style={{ background: K.surface, border: "1px solid " + K.border, borderRadius: 8, padding: "10px 14px", marginBottom: 10, fontSize: 13, color: K.inkMid }} aria-label="Invoice totals">
              <div style={{ display: "flex", justifyContent: "space-between" }}><span>Subtotal</span><span>{money(pricing.subtotal)}</span></div>
              {pricing.discount && <div style={{ display: "flex", justifyContent: "space-between" }}><span>{pricing.discount.label} ({formatPct(pricing.discount.pct)}%)</span><span>-{money(pricing.discount.amount)}</span></div>}
              <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, color: K.ink, fontSize: 15, marginTop: 4 }}><span>Total</span><span>{money(pricing.total)}</span></div>
            </div>
            {ready && creditLimit > 0 && (
              <div style={creditOver ? box(K.amberBg, K.amberBorder, K.amber) : { fontSize: 11, color: K.inkLight, marginBottom: 10 }}>
                Credit limit {money(creditLimit)}; unpaid invoices {money(outstanding)}{creditOver ? "; with this invoice the account would owe " + money(outstanding + pricing.total) + ", over the limit. You can still create it." : "."}
              </div>
            )}
            {notice && <div style={box(K.amberBg, K.amberBorder, K.amber)} role="status">{notice}</div>}
            {result && !result.ok && !(result.uncertain && pendingId) && (
              <div style={box(result.uncertain ? K.amberBg : K.redLight, result.uncertain ? K.amberBorder : K.redBorder, result.uncertain ? K.amber : K.red)} role="alert">
                {result.uncertain && <strong>Not confirmed. </strong>}{result.message}
              </div>
            )}
            {problem && !busy && <div style={{ fontSize: 11, color: K.inkLight, marginBottom: 8 }}>{problem}</div>}
          </>
        )}

        {pendingId && (
          <div style={box(K.amberBg, K.amberBorder, K.amber)} role="alert" aria-label="Invoice not confirmed">
            <div><strong>Not confirmed. </strong>{result && !result.ok && result.uncertain && result.message ? result.message : "An earlier attempt to save this invoice was not confirmed, so it may exist."}</div>
            <div style={{ fontSize: 20, fontWeight: 700, fontFamily: "monospace", color: K.ink, margin: "8px 0" }} data-testid="pending-invoice-id">{pendingId}</div>
            <div>
              Look for this invoice number in the Orders list before doing anything else. If it IS there, the invoice already exists: close this window and do not create it again.{" "}
              {onOpenInOrders && <button style={linkBtn} disabled={busy} onClick={() => { onOpenInOrders(pendingId); setConfirmClose(false); setPeek(true); }}>Open Orders</button>}
              {onOpenInOrders ? " (this window stays open: use \"Back to the invoice window\" to return)" : ""}
            </div>
            <div style={{ marginTop: 8 }}>
              <button style={btnSecondary} disabled={busy || checking} onClick={checkDb}>{checking ? "Checking the database..." : "Check the database now"}</button>
              <span style={{ fontSize: 11, marginLeft: 8 }}>Asks the database itself, not the Orders list.</span>
            </div>
            {dbCheck && dbCheck.state === "not_found" && (
              <div style={{ marginTop: 6, fontWeight: 600 }} role="status" data-testid="db-check-result">
                Checked the database just now: invoice {pendingId} is NOT there.{pendingRef.current && !pendingRef.current.ownAbort ? " A slow server can still finish saving for a while after a timeout, so Create waits 15 seconds and checks again before it saves anything." : ""}
              </div>
            )}
            {dbCheck && dbCheck.state === "in_flight" && (
              <div style={{ marginTop: 6, fontWeight: 600 }} role="status" data-testid="db-check-result">
                The database does not have invoice {pendingId} yet, but the earlier attempt from this page is still running and may yet be saved. Wait a minute, then check again.
              </div>
            )}
            {dbCheck && dbCheck.state === "found_unreadable" && (
              <div style={{ marginTop: 6, fontWeight: 600, color: K.red }} role="alert" data-testid="db-check-result">
                Invoice {pendingId} IS in the database{dbCheck.customer ? " (" + dbCheck.customer + (dbCheck.total != null ? ", " + money(dbCheck.total) : "") + ")" : ""}, but its details could not be read to show them here. Do NOT create it again: close this window and look at it in Orders.
              </div>
            )}
            {dbCheck && (dbCheck.state === "could_not_check" || dbCheck.state === "unexpected_row") && (
              <div style={{ marginTop: 6, fontWeight: 600, color: K.red }} role="alert" data-testid="db-check-result">{dbCheck.message}</div>
            )}
            <div style={{ marginTop: 8 }}>
              The Orders list can be out of date after a connection problem: an invoice that was saved while your connection was down may not show in it yet. "Check the database now" is the reliable way to look.
            </div>
            <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, cursor: busy ? "not-allowed" : "pointer", fontWeight: 600 }}>
              <input type="checkbox" aria-label="I checked Orders: this invoice number is not there" checked={checkedOrders} disabled={busy || checking} onChange={(e) => confirmChecked(e.target.checked)} />
              I checked Orders (or the database): invoice {pendingId} is not there
            </label>
            <div style={{ marginTop: 4, fontSize: 11 }} data-testid="tick-hint">Use "Check the database now" first: it asks the database directly.</div>
            {!checkedOrders && <div style={{ marginTop: 4 }}>Create invoice and Create &amp; email dealer stay disabled until you tick this.</div>}
            <div style={{ marginTop: 10, borderTop: "1px solid " + K.amberBorder, paddingTop: 8 }}>
              {!confirmRestart
                ? <button style={linkBtn} disabled={busy || checking} onClick={() => setConfirmRestart(true)}>Start over with a new invoice number</button>
                : (
                  <div role="alertdialog" aria-label="Start over with a new invoice number">
                    <div>Starting over gives this window a NEW invoice number. The old number <strong>{pendingId}</strong> is not deleted and may already exist: before you continue, look for <strong>{pendingId}</strong> in the Orders list. If it is there, that invoice is real (void it in Orders if you do not want it).</div>
                    <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                      <button style={btnPrimary} disabled={busy || checking} onClick={startOver}>Yes, start over with a new number</button>
                      <button style={btnSecondary} onClick={() => setConfirmRestart(false)}>Keep {pendingId}</button>
                    </div>
                  </div>
                )}
            </div>
          </div>
        )}

        {busy && waitInfo && waitLeft > 0 && (
          <div style={box(K.amberBg, K.amberBorder, K.amber)} role="status" aria-label="Waiting before checking again" data-testid="retry-countdown">
            <strong>Checking again in {waitLeft} s.</strong> The earlier attempt for invoice {pendingId || "this invoice"} may still be finishing on the server, so Create waits before it looks the number up again. This is what stops the invoice being saved twice.
          </div>
        )}
        {busy && waitInfo && waitLeft <= 0 && (
          <div style={box(K.amberBg, K.amberBorder, K.amber)} role="status" aria-label="Checking the database now" data-testid="retry-checking">
            <strong>Checking the database now...</strong> The wait is over: Create is looking invoice {pendingId || "this invoice"} up and, only if it is not there, saving it. On a slow connection this can take a little while: please do not close or reload this page.
          </div>
        )}
        {confirmClose && pendingId && (
          <div ref={confirmCloseRef} style={box(K.redLight, K.redBorder, K.red)} role="alertdialog" aria-label="Close without confirming the invoice">
            <div><strong>Invoice {pendingId} was not confirmed.</strong> It may already be saved. If you close this window the number is forgotten.</div>
            <div style={{ marginTop: 4 }}>Before you close, press "Check the database now" or look for <strong>{pendingId}</strong> in Orders.{oldNumbers.length > 0 ? " You also started over earlier and left " + oldList + " unconfirmed: look for " + (oldNumbers.length > 1 ? "each of them" : "it") + " in Orders too." : ""}</div>
            <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
              <button style={btnPrimary} onClick={() => setConfirmClose(false)}>Keep window open</button>
              <button style={btnSecondary} disabled={busy || checking} onClick={closeAnyway}>Close anyway</button>
            </div>
          </div>
        )}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
          <button style={{ ...btnPrimary, opacity: !ready || problem || busy || awaitingCheck || checking ? 0.5 : 1 }} disabled={!ready || !!problem || busy || awaitingCheck || checking} onClick={() => submit(false)}>{busy ? "Saving..." : "Create invoice"}</button>
          <button style={{ ...btnPrimary, background: K.green, opacity: !ready || problem || busy || !canEmail || awaitingCheck || checking ? 0.5 : 1 }} disabled={!ready || !!problem || busy || !canEmail || awaitingCheck || checking} onClick={() => submit(true)} title={canEmail ? "Saves the invoice, then emails it to " + dealerEmail : "This dealer has no valid email address on file"}>
            {busy ? "Saving..." : "Create & email dealer"}
          </button>
          <button style={btnSecondary} disabled={busy || checking} onClick={close}>Cancel</button>
        </div>
        {ready && <div style={{ fontSize: 11, color: K.inkLight, marginTop: 8 }}>"Create & email dealer" sends to {dealerEmail || "(no email on file)"}. The credited rep sees the invoice in their portal and is not emailed.</div>}
      </div>
    </div>
  );
}
